// ==========================================
// Workflow step card (3a / 1d)
// ==========================================
// Renders inline in the chat log: name, live progress, steps checking off one by
// one, and cancel. On failure it swaps to a recovery row with retry/edit rather
// than opening a dialog — no modals is a hard guardrail (2d).
// ==========================================

import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';

import { WorkflowRun, WorkflowStep } from '../../models/buddy.types';

@Component({
  selector: 'app-buddy-workflow-card',
  standalone: false,
  templateUrl: './buddy-workflow-card.component.html',
  styleUrls: ['./buddy-workflow-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BuddyWorkflowCardComponent {
  @Input() run!: WorkflowRun;

  @Output() cancelled = new EventEmitter<string>();
  @Output() retried = new EventEmitter<string>();
  /** The user's Yes/No on a paused (awaiting_approval) run. */
  @Output() decided = new EventEmitter<{ runId: string; approved: boolean }>();

  /** Which step's log is expanded — tap a step to open it (1d). */
  expandedStepId: string | null = null;

  get doneCount(): number {
    return this.run.steps.filter((s) => s.status === 'done').length;
  }

  get progressPct(): number {
    if (!this.run.steps.length) {
      return 0;
    }
    return Math.round((this.doneCount / this.run.steps.length) * 100);
  }

  get statusLine(): string {
    switch (this.run.status) {
      case 'running':
        return `running · ${Math.min(this.doneCount + 1, this.run.steps.length)} of ${this.run.steps.length}`;
      case 'awaiting_approval':
        return 'waiting for you';
      case 'done':
        return `done · ${this.run.steps.length} of ${this.run.steps.length}`;
      case 'failed':
        return 'failed';
      case 'cancelled':
        return 'cancelled';
      default:
        return '';
    }
  }

  /** One preview line for an approval item — join its values ("423-OPP — Cristian Dawis"). */
  itemLine(item: Record<string, unknown>): string {
    return Object.values(item)
      .map((v) => String(v))
      .join(' — ');
  }

  decide(approved: boolean): void {
    this.decided.emit({ runId: this.run.id, approved });
  }

  glyphFor(step: WorkflowStep): string {
    switch (step.status) {
      case 'done':
        return '✓';
      case 'running':
        return '◍';
      case 'failed':
        return '✕';
      default:
        return '·';
    }
  }

  toggleStep(step: WorkflowStep): void {
    this.expandedStepId = this.expandedStepId === step.id ? null : step.id;
  }

  trackStep(_: number, step: WorkflowStep): string {
    return step.id;
  }
}
